import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  TextField as MuiTextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button, TextField, TextView } from '@atoms';
import { toast } from 'react-toastify';
import { useReplaceArtifactContent, materialTheme } from '@particles';
import FileUploader from './create/FileUploader';
import HtmlTextInput from './create/HtmlTextInput';
import { processHtmlContent } from './create/processHtmlContent';

interface ReplaceArtifactDialogProps {
  open: boolean;
  artifact: Artifact | null;
  onClose: () => void;
}

const DEFAULT_TITLE = 'SalesRook Report';
const DEFAULT_DESCRIPTION =
  'SalesRook - Advanced sales analytics and reporting platform for data-driven business growth';

const ReplaceArtifactDialog: React.FC<ReplaceArtifactDialogProps> = ({ open, artifact, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [htmlContent, setHtmlContent] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [includeHeaderFooter, setIncludeHeaderFooter] = useState(true);

  const replaceMutation = useReplaceArtifactContent();

  useEffect(() => {
    if (open && artifact) {
      setHtmlContent('');
      setTitle(artifact.title || '');
      setDescription(artifact.description || '');
      setIncludeHeaderFooter(true);
      setActiveTab(0);
    }
  }, [open, artifact]);

  const handleClose = () => {
    if (replaceMutation.isPending) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!artifact) return;
    if (!htmlContent) {
      toast.error('Please add the new HTML content (upload a file or paste it)');
      return;
    }

    const shareId = artifact.fileName;
    const baseUrl = process.env.REACT_APP_API_BASE || '';
    const shareUrl = `${baseUrl}/a/${shareId}`;
    const reportTitle = title || DEFAULT_TITLE;
    const reportDescription = description || DEFAULT_DESCRIPTION;

    const processedHtml = processHtmlContent(
      htmlContent,
      shareUrl,
      reportTitle,
      reportDescription,
      includeHeaderFooter
    );

    const formData = new FormData();
    const blob = new Blob([processedHtml], { type: 'text/html' });
    formData.append('file', blob, `${shareId}-${Date.now()}.html`);
    formData.append('title', reportTitle);
    formData.append('description', reportDescription);

    replaceMutation.mutate(
      { id: artifact.id, formData },
      {
        onSuccess: () => {
          toast.success('Artifact updated. Existing share link continues to work.');
          onClose();
        },
        onError: (error: unknown) => {
          const errorMessage = error && typeof error === 'object' && 'error' in error
            ? String((error as { error: unknown }).error)
            : 'Unknown error';
          toast.error(`Failed to replace artifact: ${errorMessage}`);
        }
      }
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <UploadFileIcon sx={{ mr: 1, color: materialTheme.palette.primary.main }} />
          <TextView variant="h6" $fontWeight={600}>
            Replace artifact HTML
          </TextView>
        </Box>
        <TextView variant="caption" color="textSecondary">
          The shared link {artifact ? `(/a/${artifact.fileName})` : ''} stays the same.
        </TextView>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ mb: 1 }}
            TabIndicatorProps={{ sx: { height: 2 } }}>
            <Tab label="Upload file" />
            <Tab label="Paste HTML" />
          </Tabs>
          {activeTab === 0 ? (
            <FileUploader onHtmlContent={setHtmlContent} />
          ) : (
            <HtmlTextInput onHtmlContent={setHtmlContent} />
          )}
          {htmlContent && (
            <TextView variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
              ✓ New HTML loaded ({(htmlContent.length / 1024).toFixed(1)} KB). Click Save to apply.
            </TextView>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            label="Title"
            name="replaceTitle"
            fullWidth={true}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Artifact title"
          />
          <MuiTextField
            label="Description"
            name="replaceDescription"
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Artifact description"
            multiline
            rows={2}
            size="small"
            sx={{ mt: 2 }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={includeHeaderFooter}
                onChange={(e) => setIncludeHeaderFooter(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<TextView variant="body2">Include SalesRook header & footer</TextView>}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={handleClose} disabled={replaceMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={replaceMutation.isPending}
          disabled={!htmlContent || replaceMutation.isPending}
          startIcon={<UploadFileIcon />}>
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReplaceArtifactDialog;
